import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Card, CircularProgress, Grid, Pagination, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useUserGroups } from '../../../hooks/useGroups';
import { getPanelRole } from '../../../services/api';
import { useRole } from '../../../utils/RoleContext';
import GroupCard from './components/GroupCard';
import GroupsPageHeader, {
    ACTIVE_GROUPS_SECTION,
    COMPLETED_GROUPS_SECTION
} from './components/GroupsPageHeader';

const MyGroups = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const roleUuid = getPanelRole();
    const { role } = useRole();
    const [selectedSection, setSelectedSection] = useState(ACTIVE_GROUPS_SECTION);
    const activeGroups = selectedSection === ACTIVE_GROUPS_SECTION;

    const {
        data: groups,
        isLoading,
        error,
        count,
        paginationModel,
        setPaginationModel
    } = useUserGroups(roleUuid, activeGroups);

    const handleSectionChange = (nextSection) => {
        setSelectedSection(nextSection);
        setPaginationModel((previous) => ({ ...previous, page: 0 }));
    };

    const handlePageChange = (_, newPage) => {
        setPaginationModel((previous) => ({ ...previous, page: newPage - 1 }));
    };

    return (
        <Box sx={{ minHeight: 'calc(100vh - 80px)', p: 2, bgcolor: '#F4F6F8' }}>
            <GroupsPageHeader
                selectedSection={selectedSection}
                onSectionChange={handleSectionChange}
                onAddGroup={() => navigate('/grupos/criar')}
            />

            <Card
                variant="outlined"
                sx={{
                    p: 4,
                    borderColor: '#E0E5E9',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
                }}
            >
                <Grid container spacing={2}>
                    {isLoading ? (
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" height="300px">
                                <CircularProgress sx={{ color: '#1976d2' }} />
                            </Box>
                        </Grid>
                    ) : error ? (
                        <Grid item xs={12}>
                            <Typography color="error" align="center">
                                {t('groups.load_error')}
                            </Typography>
                        </Grid>
                    ) : groups.length > 0 ? (
                        <>
                            {groups.map((group) => (
                                <Grid item xs={12} md={6} key={group.uuid}>
                                    <GroupCard
                                        group={group}
                                        onView={
                                            role === 'DOCTOR'
                                                ? () => navigate(`/grupos/${group.uuid}`)
                                                : undefined
                                        }
                                        onEdit={
                                            role === 'DOCTOR'
                                                ? () =>
                                                      navigate(
                                                          `/grupos/editar/${group.uuid}`
                                                      )
                                                : undefined
                                        }
                                        isFinished={
                                            selectedSection === COMPLETED_GROUPS_SECTION
                                        }
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="center" mt={4}>
                                    <Pagination
                                        count={Math.ceil(
                                            count / paginationModel.pageSize
                                        )}
                                        page={paginationModel.page + 1}
                                        onChange={handlePageChange}
                                    />
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12}>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                align="center"
                            >
                                {activeGroups
                                    ? t('groups.no_groups.active')
                                    : t('groups.no_groups.completed')}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Card>
        </Box>
    );
};

export default MyGroups;
